import React, { useState, useEffect } from "react";
import { Col, Container, Row, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import axios from "axios";
import List from "../../components/listing/List";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import Tr from "../../components/tr/Tr";

const LEASE_STATUS_OPTIONS = [
  "PENDING",
  "IN_REVIEW",
  "APPROVED",
  "REJECTED",
  "COMPLETED",
];

const Lease = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token = loggedInUser?.refreshToken || authState?.refreshToken || loggedInUser?.token || authState?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const handleStatusChange = async (leaseId, newStatus) => {
    try {
      const response = await axios.put(
        `${baseUrl}/lease/status`,
        {
          lease_id: Number(leaseId),
          status: newStatus,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.data?.statusType === "SUCCESS") {
        showToast("Lease status updated successfully!", { type: "success" });
        setData((prev) =>
          prev.map((item) => (item.id === leaseId ? { ...item, status: newStatus } : item))
        );
      } else {
        showToast(response.data?.text || "Failed to update status", { type: "error" });
      }
    } catch (error) {
      console.error("Error updating lease status:", error);
      showToast("Error updating lease status", { type: "error" });
    }
  };

  const columns = [
    { displayName: "S.No.", render: (item, index) => (currentPage - 1) * 10 + index + 1 },
    { displayName: "Title", key: "title" },
    {
      displayName: "Purpose",
      render: (item) => item.purpose?.name || item.purpose_name || item.purpose || "-",
    },
    {
      displayName: "Budget",
      render: (item) => (item.budget ? `₹ ${Number(item.budget).toLocaleString("en-IN")}` : "-"),
    },
    {
      displayName: "Location",
      render: (item) => [item.city, item.state].filter(Boolean).join(", ") || "-",
    },
    {
      displayName: "Status",
      render: (item) => (
        <Form.Select
          size="sm"
          value={item.status || "PENDING"}
          onChange={(e) => handleStatusChange(item.id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{ width: "130px", fontSize: "12px", fontWeight: "600" }}
        >
          {LEASE_STATUS_OPTIONS.map((st) => (
            <option key={st} value={st}>
              {st}
            </option>
          ))}
        </Form.Select>
      ),
    },
  ];

  const fetchLeases = async (pageNumber = 1) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/lease/all`,
        {
          page_number: pageNumber,
          page_size: 10,
          order_by: "DESC",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const resData = response.data;
      const list =
        resData?.leases ||
        resData?.data ||
        resData?.content ||
        resData?.leaseList ||
        (Array.isArray(resData) ? resData : []);

      setData(list);
      setTotalPages(resData?.totalPage || resData?.total_page || 1);
    } catch (error) {
      console.error("Error fetching leases:", error);
      showToast("Error fetching leases", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeases(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleDelete = async (itemId) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/lease/delete/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.data?.statusType === "SUCCESS") {
        setData((prev) => prev.filter((item) => item.id !== itemId));
        showToast("Lease deleted successfully", { type: "success" });
      } else {
        showToast(response.data?.text || "Failed to delete lease", { type: "error" });
      }
    } catch (error) {
      console.error("Error deleting lease:", error);
      showToast("Error occurred during deletion", { type: "error" });
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Lease Management", link: "/lease" },
    { label: "Leases", link: "/lease", active: true },
  ];

  return (
    <>
      <Notification />
      <div className="content-body2">
        <Container>
          <BreadCrumb items={breadcrumbItems} />

          <Row>
            <Col lg={12} md={12} sm={12}>
              {data?.length === 0 && !isLoading ? (
                <Tr
                  columns={columns}
                  initialValues={{}}
                  title="No Leases Found"
                  buttonName="Create"
                  btnLink="lease/add-lease"
                  titleHeader="Leases"
                />
              ) : (
                <List
                  title="Leases"
                  data={data}
                  columns={columns}
                  button="Create"
                  route="lease"
                  to="add-lease"
                  handleDelete={handleDelete}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Lease;
